/**
 * SessionMonitor — single handler for session expiry (401 after auth).
 * Mirrors ImpersonationExpiryMonitor: redirect once, show one notice on login.
 */

import SnackbarService from '@/services/SnackbarService.js';

const STORAGE_KEY = 'sessionMonitorState';
const SESSION_EXPIRED_QUERY_PARAM = 'session_expired';
const SESSION_EXPIRED_MESSAGE =
    'Your session has expired. Please log in again to continue. Any unsaved work will be lost.';
const PUBLIC_PATHS = ['/', '/login', '/password/email', '/password/reset'];

const getStore = () => window.$nuxt?.$store || window.$store;

class SessionMonitor {
    constructor() {
        this._state = {
            sessionTimedOut: false,
            notificationShown: false,
            redirectInProgress: false,
            timeoutDetectedAt: null,
        };

        // Fresh page load must not inherit a prior tab's timed-out flags or
        // concurrent 401s would be suppressed with no redirect/notice.
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (_error) {
            // ignore
        }

        window.addEventListener('storage', event => this.handleStorageChange(event));

        if (!this.isPublicPath(window.location.pathname)) {
            setTimeout(() => this.setupStoreWatcher(), 0);
        }
    }

    setupStoreWatcher() {
        const store = getStore();
        if (!store) {
            return;
        }

        store.watch(
            state => state.Auth?.sessionTimedOut,
            newVal => {
                if (newVal === true) {
                    this.handleSessionTimeout();
                }
            }
        );
    }

    handleStorageChange(event) {
        if (event.key !== STORAGE_KEY || !event.newValue) {
            return;
        }

        try {
            const shared = JSON.parse(event.newValue);
            if (shared.sessionTimedOut && !this._state.sessionTimedOut) {
                this.handleSessionTimeout();
            }
        } catch (_error) {
            // ignore
        }
    }

    isPublicPath(path = window.location.pathname) {
        return PUBLIC_PATHS.some(p => path === p || (p !== '/' && path.startsWith(`${p}/`)));
    }

    /** Suppress duplicate unauthorized snackbars once expiry is being handled. */
    shouldSuppressError(error) {
        return error?.response?.status === 401 && (this._state.sessionTimedOut || this._state.redirectInProgress);
    }

    handleSessionTimeout() {
        if (this._state.redirectInProgress || this.isPublicPath()) {
            return;
        }

        if (this._state.sessionTimedOut) {
            this.redirectToLogin();
            return;
        }

        this._state.sessionTimedOut = true;
        this._state.notificationShown = true;
        this._state.timeoutDetectedAt = new Date().toISOString();
        this.persistState();

        window.dispatchEvent(new CustomEvent('session-timeout'));
        this.redirectToLogin();
    }

    /** One lasting notice after landing on login (same pattern as ImpersonationExpiryMonitor). */
    maybeShowNotification(router) {
        const url = new URL(window.location.href);

        if (!url.searchParams.has(SESSION_EXPIRED_QUERY_PARAM)) {
            return;
        }

        url.searchParams.delete(SESSION_EXPIRED_QUERY_PARAM);

        // Sync via $router.replace({ query }) — same pattern as Remediations.vue.
        // Build query from the cleaned URL so sibling params (e.g. redirect) are kept;
        // history.replaceState alone leaves Vue Router able to restore the flag.
        if (router) {
            const query = Object.fromEntries(url.searchParams.entries());
            router.replace({ query }).catch(() => {});
        } else {
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }

        this.reset();

        SnackbarService.open({
            message: SESSION_EXPIRED_MESSAGE,
            type: 'is-warning',
            position: 'is-top',
            duration: 10000,
            queue: false,
        });
    }

    isNotificationShown() {
        return this._state.notificationShown;
    }

    isRedirectInProgress() {
        return this._state.redirectInProgress;
    }

    isSessionTimedOut() {
        return this._state.sessionTimedOut;
    }

    markRedirectInProgress() {
        this._state.redirectInProgress = true;
    }

    reset() {
        this._state = {
            sessionTimedOut: false,
            notificationShown: false,
            redirectInProgress: false,
            timeoutDetectedAt: null,
        };

        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (_error) {
            // ignore
        }
    }

    redirectToLogin() {
        if (this._state.redirectInProgress) {
            return;
        }

        this._state.redirectInProgress = true;

        const currentPath = window.location.pathname + window.location.search + window.location.hash;
        let loginUrl = `/login?${SESSION_EXPIRED_QUERY_PARAM}=1`;
        const pathOnly = window.location.pathname;

        if (pathOnly !== '/login' && pathOnly !== '/') {
            loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}&${SESSION_EXPIRED_QUERY_PARAM}=1`;

            try {
                getStore()?.dispatch('Auth/saveReturnPath', pathOnly);
            } catch (_error) {
                // ignore
            }
        }

        try {
            window.location.replace(loginUrl);
        } catch (_error) {
            window.location.href = loginUrl;
        }
    }

    persistState() {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    sessionTimedOut: this._state.sessionTimedOut,
                    notificationShown: this._state.notificationShown,
                    timeoutDetectedAt: this._state.timeoutDetectedAt,
                })
            );
        } catch (_error) {
            // ignore
        }
    }
}

const sessionMonitor = new SessionMonitor();
window.SessionMonitor = sessionMonitor;

export default sessionMonitor;
export { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_QUERY_PARAM };
